import { useState } from "react";
import {
  useCreateProjectTask,
  useDeleteProjectTask,
  useListProjectTasks,
  useUpdateProjectTask,
  type ProjectTaskColumn,
  type ProjectTaskRecord,
} from "@vorloop/api-client-react";
import {
  IconPlus,
  IconSidebarToggle,
  IconX,
} from "../layout/Icons";

/**
 * "Background Tasks" rail per Replit's left-panel pattern. Sits above
 * the ChatDock in the editor's left split. Lets users queue agent
 * tasks that the agent picks up off the chat critical path. Real
 * tasks live in /api/projects/:id/tasks; the background worker that
 * actually runs them is still TODO.
 */

export type ProjectTask = ProjectTaskRecord;

export function TaskRail({
  projectId,
  projectName,
  onOpenBoard,
  collapsed: collapsedProp,
  onToggleCollapsed,
  boardOpen = false,
}: {
  projectId: string;
  projectName: string;
  /** Called when the user clicks the bottom toggle button. The same
   * handler covers both "Open task board" and "Close task board" —
   * the label flips based on {@link boardOpen}. */
  onOpenBoard: () => void;
  /** Optional controlled collapsed state. If omitted, the rail manages its own. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** When true, the rail is rendered inside the TaskBoard overlay —
   * the bottom button reads "Close task board" instead of "Open task
   * board", and clicking it triggers `onOpenBoard()` to close. */
  boardOpen?: boolean;
}) {
  const tutorialKey = `vp.tasks.tutorial.${projectId}`;
  const collapsedKey = "vp.tasks.rail.collapsed";
  const { data } = useListProjectTasks(projectId);
  const tasks = data?.items ?? [];
  const create = useCreateProjectTask(projectId);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerDraft, setComposerDraft] = useState("");
  const [showTutorial, setShowTutorial] = useState(() => {
    try {
      return localStorage.getItem(tutorialKey) !== "dismissed";
    } catch {
      return true;
    }
  });
  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    try {
      return localStorage.getItem(collapsedKey) === "1";
    } catch {
      return false;
    }
  });
  const collapsed = collapsedProp ?? internalCollapsed;

  function toggleCollapsed() {
    if (onToggleCollapsed) {
      onToggleCollapsed();
      return;
    }
    setInternalCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem(collapsedKey, next ? "1" : "0");
      } catch {
        // ignore
      }
      return next;
    });
  }

  if (collapsed) {
    return (
      <aside
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          // Bottom padding clears the absolutely positioned sidebar-toggle
          // (44px = 10px from edge + 32px button + 2px slack).
          padding: "10px 6px 52px",
          background: "var(--bg-panel)",
          width: 50,
          height: "100%",
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={onOpenBoard}
          aria-label="New task"
          title="New task"
          style={iconRailBtn}
        >
          <IconPlus size={14} />
        </button>
        <button
          type="button"
          onClick={onOpenBoard}
          aria-label={boardOpen ? "Close task board" : "Open task board"}
          title={boardOpen ? "Close task board" : "Open task board"}
          style={iconRailBtn}
        >
          {boardOpen ? "×" : "◰"}
        </button>
        {tasks.length > 0 && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: "var(--text-tertiary)",
              fontVariantNumeric: "tabular-nums",
            }}
            title={`${tasks.length} task${tasks.length === 1 ? "" : "s"}`}
          >
            {tasks.length}
          </span>
        )}
        <SidebarToggleButton
          ariaLabel="Show task bar"
          ariaPressed={false}
          title="Show task bar"
          onClick={toggleCollapsed}
        />
      </aside>
    );
  }

  function dismissTutorial() {
    setShowTutorial(false);
    try {
      localStorage.setItem(tutorialKey, "dismissed");
    } catch {
      // ignore
    }
  }

  return (
    <aside
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        background: "var(--bg-panel)",
        width: 260,
        height: "100%",
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      {/* Header label. The sidebar-toggle button at the footer is the
          single control for collapsing — no duplicate arrow here. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px 14px 8px",
          borderBottom: "1px solid var(--border-default)",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: "var(--text-tertiary)",
          }}
        >
          Background tasks
        </span>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "12px 12px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {/* Version + project context */}
        <div
          style={{
            padding: "10px 12px",
            background: "var(--bg-panel-2)",
            border: "1px solid var(--border-default)",
            borderRadius: 10,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              cursor: "default",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 14,
                height: 14,
                borderRadius: 999,
                border: "2px solid var(--accent-blue)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: "var(--accent-blue)",
                }}
              />
            </span>
            <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>
              Main version
            </span>
          </label>
          <span
            style={{
              fontSize: 11,
              color: "var(--text-tertiary)",
              paddingLeft: 22,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={projectName}
          >
            {projectName}
          </span>
        </div>

        {/* Primary action — clicking the button reveals an inline
            composer right here instead of jumping to the board. */}
        {composerOpen ? (
          <form
            data-testid="task-rail-inline-composer"
            onSubmit={(e) => {
              e.preventDefault();
              const trimmed = composerDraft.trim();
              if (!trimmed) return;
              create.mutate({ prompt: trimmed });
              setComposerDraft("");
              setComposerOpen(false);
            }}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              padding: 10,
              background: "var(--bg-panel-2)",
              border: "1px solid var(--border-default)",
              borderRadius: 10,
            }}
          >
            <textarea
              autoFocus
              value={composerDraft}
              onChange={(e) => setComposerDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  setComposerDraft("");
                  setComposerOpen(false);
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  e.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder="Plan a new task… (Enter to add, Esc to cancel)"
              rows={2}
              style={{
                width: "100%",
                background: "var(--bg-canvas)",
                border: "1px solid var(--border-default)",
                borderRadius: 7,
                color: "var(--text-primary)",
                fontSize: 12.5,
                padding: "6px 8px",
                resize: "vertical",
                outline: "none",
                fontFamily: "inherit",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <button
                type="button"
                onClick={() => {
                  setComposerDraft("");
                  setComposerOpen(false);
                }}
                style={{
                  padding: "5px 10px",
                  background: "transparent",
                  border: "1px solid var(--border-default)",
                  borderRadius: 6,
                  color: "var(--text-secondary)",
                  fontSize: 11.5,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!composerDraft.trim()}
                style={{
                  padding: "5px 12px",
                  background: composerDraft.trim() ? "var(--accent-blue)" : "var(--bg-active)",
                  color: composerDraft.trim() ? "#fff" : "var(--text-tertiary)",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 11.5,
                  fontWeight: 500,
                  cursor: composerDraft.trim() ? "pointer" : "not-allowed",
                }}
              >
                Add task
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            data-testid="task-rail-new-task"
            onClick={() => setComposerOpen(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "9px 12px",
              background: "var(--accent-blue)",
              border: "none",
              borderRadius: 8,
              color: "#fff",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <IconPlus size={13} />
            New task
          </button>
        )}

        {showTutorial && tasks.length === 0 && (
        <div
          style={{
            position: "relative",
            padding: 12,
            border: "1px solid var(--border-default)",
            background: "var(--bg-panel-2)",
            borderRadius: 10,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={dismissTutorial}
            aria-label="Dismiss"
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              background: "transparent",
              border: "none",
              color: "var(--text-tertiary)",
              cursor: "pointer",
              padding: 2,
              display: "inline-flex",
            }}
          >
            <IconX size={10} />
          </button>
          <span
            style={{
              alignSelf: "flex-start",
              padding: "1px 8px",
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              background: "rgba(99,102,241,0.18)",
              color: "#a5b4fc",
              borderRadius: 999,
            }}
          >
            New
          </span>
          <div style={{ display: "flex", gap: 4 }}>
            {[
              "#6366f1",
              "#3b82f6",
              "#22c55e",
              "#eab308",
              "#a855f7",
            ].map((c, i) => (
              <span
                key={i}
                aria-hidden
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 5,
                  background: c,
                  opacity: 0.85,
                  display: "inline-block",
                }}
              />
            ))}
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 11.5,
              color: "var(--text-secondary)",
              lineHeight: 1.45,
            }}
          >
            Background tasks allow you to get more work done at once.
          </p>
          <p style={{ margin: 0, fontSize: 11.5, color: "var(--text-secondary)" }}>
            Try creating your first one!
          </p>
          <a
            href="https://github.com/devwadahai/vorloop-dragn#background-tasks"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11.5, color: "var(--accent-blue)" }}
          >
            View documentation
          </a>
        </div>
      )}

        {tasks.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: "var(--text-tertiary)",
              }}
            >
              Recent
            </div>
            <ol
              style={{
                margin: 0,
                padding: 0,
                listStyle: "none",
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              {tasks.slice(0, 8).map((t) => (
                <li key={t.id} style={{ listStyle: "none" }}>
                  <button
                    type="button"
                    data-testid="task-rail-recent-task"
                    onClick={() => {
                      // Jump to the board on click. The board opens with
                      // every column visible; the user moves on from there.
                      if (!boardOpen) onOpenBoard();
                    }}
                    title={`${t.prompt} — open task board`}
                    style={{
                      width: "100%",
                      padding: "7px 10px",
                      background: "var(--bg-panel-2)",
                      border: "1px solid var(--border-default)",
                      borderRadius: 8,
                      fontSize: 12,
                      color: "var(--text-secondary)",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: COLUMN_COLOR[t.column],
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t.prompt}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {/* Footer pinned to the bottom: sidebar toggle + Open task board.
          Filtering belongs on the board itself (columns are the filter)
          — the rail just surfaces recent activity. */}
      <div
        style={{
          borderTop: "1px solid var(--border-default)",
          padding: "10px 12px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flexShrink: 0,
        }}
      >
        {/* Left padding clears the absolutely positioned sidebar-toggle
            (32px button + 6px gap = 38px). */}
        <div style={{ display: "flex", alignItems: "center", paddingLeft: 38 }}>
          <button
            type="button"
            onClick={onOpenBoard}
            data-testid="task-board-toggle"
            aria-label={boardOpen ? "Close task board" : "Open task board"}
            title={boardOpen ? "Close task board (Esc)" : "Open task board"}
            style={{
              flex: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "8px 12px",
              background: "var(--bg-panel-2)",
              border: "1px solid var(--border-default)",
              borderRadius: 8,
              color: "var(--text-primary)",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <span aria-hidden style={{ fontSize: 14 }}>{boardOpen ? "×" : "◰"}</span>
            {boardOpen ? "Close task board" : "Open task board"}
          </button>
        </div>
      </div>
      <SidebarToggleButton
        ariaLabel="Hide task bar"
        ariaPressed
        title="Hide task bar"
        onClick={toggleCollapsed}
      />
    </aside>
  );
}

const iconRailBtn: React.CSSProperties = {
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "1px solid var(--border-default)",
  borderRadius: 7,
  color: "var(--text-secondary)",
  cursor: "pointer",
  fontSize: 12,
};

/**
 * The "Hide / Show task bar" toggle. Always pinned to the same pixel
 * coordinates ({@link SIDEBAR_TOGGLE_LEFT}, {@link SIDEBAR_TOGGLE_BOTTOM})
 * relative to the rail container so it stays visually anchored across
 * collapsed ↔ expanded transitions. The rail container must be
 * position:relative.
 */
const SIDEBAR_TOGGLE_LEFT = 9;
const SIDEBAR_TOGGLE_BOTTOM = 10;
const SIDEBAR_TOGGLE_SIZE = 32;

function SidebarToggleButton({
  ariaLabel,
  ariaPressed,
  title,
  onClick,
}: {
  ariaLabel: string;
  ariaPressed: boolean;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="task-rail-sidebar-toggle"
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      title={title}
      style={{
        position: "absolute",
        left: SIDEBAR_TOGGLE_LEFT,
        bottom: SIDEBAR_TOGGLE_BOTTOM,
        width: SIDEBAR_TOGGLE_SIZE,
        height: SIDEBAR_TOGGLE_SIZE,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg-panel-2)",
        border: "1px solid var(--border-default)",
        borderRadius: 8,
        color: "var(--text-secondary)",
        cursor: "pointer",
        flexShrink: 0,
        zIndex: 2,
      }}
    >
      <IconSidebarToggle size={14} />
    </button>
  );
}

const COLUMN_COLOR: Record<ProjectTask["column"], string> = {
  draft: "#9ca3af",
  active: "#3b82f6",
  ready: "#eab308",
  done: "#22c55e",
};

export function useProjectTasks(projectId: string): {
  tasks: ProjectTask[];
  add: (prompt: string) => void;
  move: (id: string, to: ProjectTaskColumn) => void;
  remove: (id: string) => void;
} {
  const { data } = useListProjectTasks(projectId);
  const create = useCreateProjectTask(projectId);
  const update = useUpdateProjectTask(projectId);
  const del = useDeleteProjectTask(projectId);
  const tasks = (data?.items ?? []) as ProjectTask[];
  return {
    tasks,
    add: (prompt) => {
      const trimmed = prompt.trim();
      if (!trimmed) return;
      create.mutate({ prompt: trimmed });
    },
    move: (id, to) => update.mutate({ id, column: to }),
    remove: (id) => del.mutate({ id }),
  };
}
